import { Link } from "gatsby"
import React from "react"
import SideBar from "react-sidebar"

import {
  DiscoveryPage_InternalLink,
  DiscussionForum_InternalLink,
  Lifestyle_InternalLink,
  Login_InternalLink,
  Logout_InternalLink,
  MCHRI_ExternalLink,
  MyDashboard_InternalLink,
  Profile_InternalLink,
  QuestionPromptList_InternalLink,
  Search_InternalLink,
  SelfAssessment_InternalLink,
  Signup_InternalLink,
  ChooseHabits_InternalLink,
  GoalSettingHomepage_InternalLink,
  GoalReview_InternalLink,
} from "../../utils/urls"
import OnlyWithProfile from "../QueryUserProfile/OnlyWithProfile"
import { NavSidebarExternalLink } from "../buttons"
import AppLogo from "../logos/AppLogo"
import OnlyForUser from "../registration/OnlyForUser"
import { iNavSidebar, iSidebarWrapper } from "../types"

const NavSidebarWrapper: React.FC<iSidebarWrapper> = props => {
  const { children, sidebarOpen, onToggleSidebar } = props
  return (
    <>
      <div className="hidden sm:block">
        <SideBar
          sidebar={children}
          open={sidebarOpen}
          onSetOpen={onToggleSidebar}
          children={<div />} // This is just a props requirement
          shadow={true}
          pullRight
          styles={{
            sidebar: {
              position: "fixed",
              zIndex: "1000",
            },
            root: {
              position: "undefined",
            },
            content: {
              position: "undefined",
              top: "undefined",
              left: "undefined",
              right: "undefined",
              bottom: "undefined",
            },
            overlay: {
              zIndex: "5",
            },
          }}
        />
      </div>
      <div className="block sm:hidden">
        <SideBar
          sidebar={children}
          open={sidebarOpen}
          onSetOpen={onToggleSidebar}
          children={<div />} // This is just a props requirement
          shadow={true}
          styles={{
            sidebar: {
              position: "fixed",
              zIndex: "1000",
            },
            root: {
              position: "undefined",
            },
            content: {
              position: "undefined",
              top: "undefined",
              left: "undefined",
              right: "undefined",
              bottom: "undefined",
            },
            overlay: {
              zIndex: "5",
            },
          }}
        />
      </div>
    </>
  )
}

const NavSidebarButton: React.FC<{
  title: string
  link: string
  selected: boolean
}> = ({ title, selected, link }) => {
  return (
    <Link to={link}>
      <button
        className={`flex items-center transition duration-100 width-full
                  bg-gray-100 text-site-blue-dark py-1
                  text-xl leading-none pr-2 mb-1`}
      >
        {selected && (
          <span
            style={{ height: "26px", width: "4px" }}
            className={`block ${selected && "bg-site-green"} rounded-full mr-2`}
          />
        )}
        <span className="block py-1">{title}</span>
      </button>
    </Link>
  )
}

const NavSidebar: React.FC<iNavSidebar> = props => {
  const { sidebarOpen, onToggleSidebar, links, pageTitle } = props
  return (
    <NavSidebarWrapper
      sidebarOpen={sidebarOpen}
      onToggleSidebar={onToggleSidebar}
    >
      <div className="w-56 min-h-full overflow-x-auto overflow-y-auto flex items-center bg-gray-100 px-6 py-8">
        <div>
          <div className="flex items-center mb-8">
            <AppLogo width={56} />
            <p className="ml-2 text-gray-600 text-sm border-l pl-2">
              Your <strong>personal</strong> <br /> PCOS guide
            </p>
          </div>

          <OnlyForUser
            redirectTo={null}
            functionalChildren={(isUser: boolean) => {
              if (isUser) {
                return (
                  <div>
                    <OnlyWithProfile
                      noRedirectToProfilePage={true}
                      childrenWithProfileData={data => {
                        return (
                          <NavSidebarButton
                            title="My dashboard"
                            link={
                              data && data.length
                                ? MyDashboard_InternalLink
                                : Profile_InternalLink
                            }
                            selected={pageTitle === "Dashboard"}
                          />
                        )
                      }}
                    />

                    <NavSidebarButton
                      title="My profile"
                      link={Profile_InternalLink}
                      selected={pageTitle === "My profile"}
                    />

                    <NavSidebarButton
                      title="Home"
                      link={"/"}
                      selected={pageTitle === "Home"}
                    />

                    <NavSidebarButton
                      title="Search"
                      link={Search_InternalLink}
                      selected={pageTitle === "Search"}
                    />

                    <NavSidebarButton
                      title="Explore"
                      link={DiscoveryPage_InternalLink}
                      selected={pageTitle === "Topics"}
                    />

                    <NavSidebarButton
                      title="Lifestyle"
                      link={Lifestyle_InternalLink}
                      selected={pageTitle === "Lifestyle"}
                    />

                    <NavSidebarButton
                      title="Question list"
                      link={QuestionPromptList_InternalLink}
                      selected={pageTitle === "Questions"}
                    />

                    <NavSidebarButton
                      title="Forum"
                      link={DiscussionForum_InternalLink}
                      selected={pageTitle === "Forum"}
                    />

                    <NavSidebarButton
                      title="Self assessment"
                      link={SelfAssessment_InternalLink}
                      selected={pageTitle === "Self assessment"}
                    />

                    {/* <NavSidebarButton
                      title="Goal Setting"
                      link={GoalSettingHomepage_InternalLink}
                      selected={pageTitle === "Homepage"}
                    /> */}

                    <NavSidebarButton
                      title="Log out"
                      link={Logout_InternalLink}
                      selected={false}
                    />
                  </div>
                )
              }
              return (
                <div>
                  <NavSidebarButton
                    title="My dashboard"
                    link={Login_InternalLink}
                    selected={false}
                  />

                  <NavSidebarButton
                    title="Sign up"
                    link={Signup_InternalLink}
                    selected={false}
                  />

                  <NavSidebarButton
                    title="Home"
                    link={"/"}
                    selected={pageTitle === "Home"}
                  />

                  <NavSidebarButton
                    title="Search"
                    link={Search_InternalLink}
                    selected={pageTitle === "Search"}
                  />

                  <NavSidebarButton
                    title="Explore"
                    link={DiscoveryPage_InternalLink}
                    selected={pageTitle === "Topics"}
                  />

                  <NavSidebarButton
                    title="Lifestyle"
                    link={Lifestyle_InternalLink}
                    selected={pageTitle === "Lifestyle"}
                  />

                  <NavSidebarButton
                    title="Forum"
                    link={DiscussionForum_InternalLink}
                    selected={pageTitle === "Forum"}
                  />

                  {/* <NavSidebarButton
                    title="Goal Setting"
                    link={GoalSettingHomepage_InternalLink}
                    selected={pageTitle === "Homepage"}
                  /> */}
                </div>
              )
            }}
          />

          <p className="text-left text-gray-500 text-xs mt-8">
            Powered By
            <br />
            <NavSidebarExternalLink
              to={MCHRI_ExternalLink}
              className="hover:underline"
              target="_blank"
            >
              <span>
                Monash Centre for Health
                <br />
                Research &amp; Implementation
                <br />
                <strong>(MCHRI)</strong>
              </span>
            </NavSidebarExternalLink>
          </p>
        </div>
      </div>
    </NavSidebarWrapper>
  )
}

export default NavSidebar
